import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Calendar, Clock, User, ArrowRight, Search, Sparkles, Tag, ChevronRight } from "lucide-react";
import { getBlogs, getBlogsSync } from "../api";
import SEO from "../components/SEO";
import { buildBreadcrumbSchema } from "../utils/seoSchemas";

const CATEGORIES = ["All", "Guides & Tips", "Course Spotlights", "Family Fun"];

const BlogIndex = () => {
  const [blogs, setBlogs] = useState(() => getBlogsSync());
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    getBlogs({ category: category === "All" ? undefined : category, q: search })
      .then((data) => {
        if (data) setBlogs(data);
      })
      .catch(() => {});
  }, [category, search]);

  const filteredBlogs = blogs.filter((b) => {
    const matchCategory = category === "All" || b.category?.toLowerCase() === category.toLowerCase();
    const matchSearch =
      !search.trim() ||
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.excerpt?.toLowerCase().includes(search.toLowerCase()) ||
      b.author?.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  const featured = filteredBlogs[0];
  const regular = filteredBlogs.slice(1);

  const blogSchemas = [
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog & Guides", url: "/blog" },
    ]),
  ];

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]" data-testid="blog-index-page">
      <SEO
        title="Mini Golf Blog, Guides & Spotlights | Mini Golf USA"
        description="Discover pro putting tips, blacklight golf guides, family outing reviews, and course spotlights across Alabama and the USA."
        canonicalUrl="https://minigolfalabama.com/blog"
        ogType="website"
        schema={blogSchemas}
      />
      {/* Hero Banner */}
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-[#e5243b] font-medium">Blog & Guides</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-[#e5243b] text-xs font-bold uppercase tracking-wider mb-3">
                <BookOpen className="w-3.5 h-3.5" /> Mini Golf Journal
              </div>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
                News, Guides & Course Spotlights
              </h1>
              <p className="mt-3 text-gray-300 max-w-2xl text-base">
                Discover pro putting strategies, blacklight golf guides, family entertainment reviews, and the best mini golf destinations across Alabama and the USA.
              </p>
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-[#242731] border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e5243b] transition-all"
              />
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-2 mt-8 overflow-x-auto pb-2 scrollbar-none">
            {CATEGORIES.map((cat) => {
              const active = category === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold tracking-wide transition-all whitespace-nowrap ${
                    active
                      ? "bg-[#e5243b] text-white shadow-md shadow-red-900/20"
                      : "bg-[#242731] text-gray-300 hover:bg-[#2e323e] hover:text-white border border-gray-800"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredBlogs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center max-w-xl mx-auto shadow-sm">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900">No articles found</h3>
            <p className="text-gray-500 text-sm mt-1">Try selecting a different category or clearing your search term.</p>
            <button
              onClick={() => { setCategory("All"); setSearch(""); }}
              className="mt-4 px-4 py-2 text-xs font-bold text-[#e5243b] bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Featured Post Banner */}
            {featured && (
              <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                  <div className="lg:col-span-7 relative h-72 sm:h-96 lg:h-auto overflow-hidden">
                    <img
                      src={featured.coverImage}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-white bg-[#e5243b] rounded-full shadow-md">
                        {featured.category || "Featured"}
                      </span>
                    </div>
                  </div>
                  <div className="lg:col-span-5 p-6 sm:p-10 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1 font-medium">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {featured.publishedAt}
                        </span>
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {featured.readTime || "5 min read"}
                        </span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight leading-tight group-hover:text-[#e5243b] transition-colors">
                        <Link to={`/blog/${featured.slug}`}>{featured.title}</Link>
                      </h2>
                      <p className="text-gray-600 text-sm sm:text-[15px] leading-relaxed mt-3.5 line-clamp-3">
                        {featured.excerpt}
                      </p>
                    </div>

                    <div className="pt-6 mt-6 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-red-100 text-[#e5243b] font-bold text-xs flex items-center justify-center">
                          {featured.author?.[0] || "M"}
                        </div>
                        <span className="text-xs font-semibold text-gray-800">{featured.author || "Editorial Staff"}</span>
                      </div>
                      <Link
                        to={`/blog/${featured.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#e5243b] group-hover:translate-x-1 transition-transform"
                      >
                        Read Article <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Grid of Other Articles */}
            {regular.length > 0 && (
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tight mb-6">Latest Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regular.map((blog) => (
                    <article
                      key={blog.id}
                      className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col group"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={blog.coverImage}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white bg-[#181a20]/90 backdrop-blur-md rounded-md">
                            {blog.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-6 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-2.5">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              {blog.publishedAt}
                            </span>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {blog.readTime || "4 min read"}
                            </span>
                          </div>

                          <h3 className="text-lg font-bold text-gray-900 tracking-tight group-hover:text-[#e5243b] transition-colors leading-snug line-clamp-2">
                            <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                          </h3>

                          <p className="text-gray-600 text-sm mt-2.5 line-clamp-3 leading-relaxed">
                            {blog.excerpt}
                          </p>
                        </div>

                        <div className="pt-4 mt-5 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">{blog.author}</span>
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#e5243b] hover:text-[#b91c1c] transition-colors"
                          >
                            Read More <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogIndex;
